import { SetRequestParams } from '~/core/api/RequestTypes';
import { ResponseResult } from '~/core/api/ResponseTypes';
import { isCloseEvent, SERPCloseEvent } from '~/core/events/types/eventTypes/CloseEvent';
import { GoToResultEvent, isGoToResultEvent } from '~/core/events/types/eventTypes/GoToResultEvent';
import { isOpenEvent, OpenEvent } from '~/core/events/types/eventTypes/OpenEvent';
import { isSearchEvent, SearchEvent } from '~/core/events/types/eventTypes/SearchEvent';
import { SERPEvent, SERPEventType } from '~/core/events/types/SERPEvents';
import { Action, SERP_ANALYTICS_ACTIONS_TYPE } from './actions/actionTypes';
import { CloseAction } from './actions/closeAction';
import { GoToResultAction } from './actions/goToResultAction';
import { OpenAction } from './actions/openAction';
import { SearchAction } from './actions/searchAction';

export class ActionFactory {
    public createActionFromEvent(event: SERPEvent): Action {
        switch (event.type) {
            case SERPEventType.SEARCH:
                if (!isSearchEvent(event)) {
                    throw new Error('Invalid event type');
                }
                return this.createSearchAction(event);
            case SERPEventType.GO_TO_RESULT:
                if (!isGoToResultEvent(event)) {
                    throw new Error('Invalid event type');
                }
                return this.createGoToResultAction(event);
            case SERPEventType.OPEN:
                if (!isOpenEvent(event)) {
                    throw new Error('Invalid event type');
                }
                return this.createOpenAction(event);
            case SERPEventType.CLOSE:
                if (!isCloseEvent(event)) {
                    throw new Error('Invalid event type');
                }
                return this.createCloseAction(event);
            default:
                throw new Error('Unknown event type');
        }
    }

    public createSearchAction(event: SearchEvent): SearchAction {
        return {
            action: SERP_ANALYTICS_ACTIONS_TYPE.SEARCH,
            queryString: event.detail.data.searchQuery,
            totalResultsCount: event.detail.data.totalResultsCount,
            queryData: event.detail.data.requestParams.map((requestParam: SetRequestParams) => {
                const resultsData = event.detail.data.results.find(
                    (result: ResponseResult) => result.querySetIdentifier === requestParam.name
                );
                return {
                    querySet: requestParam.name,
                    totalRecords: resultsData?.totalRecords || 0,
                    from: requestParam.from,
                    to: requestParam.from + requestParam.size,
                    filters: requestParam.filters,
                    // TODO: sorting
                };
            }),
            serpType: event.detail.data.serpType,
        };
    }

    public createGoToResultAction(event: GoToResultEvent): GoToResultAction {
        return {
            action: SERP_ANALYTICS_ACTIONS_TYPE.GO_TO_RESULT,
            url: event.detail.data.url,
            order: event.detail.data.order,
            totalResultsCount: event.detail.data.resultsCount,
            queryString: event.detail.data.searchQuery,
            querySetIdentifier: event.detail.data.resultType, // TODO: querySetIdentifier is misleading name and should be changed, this property is used to identify the type of result (e.g. "category" or "product" or "fulltext" )
            serpType: event.detail.data.serpType,
            button: event.detail.data.button,
            key: event.detail.data.key,
        };
    }

    public createOpenAction(event: OpenEvent): OpenAction {
        return {
            action: SERP_ANALYTICS_ACTIONS_TYPE.OPEN,
            serpType: event.detail.data.serpType,
        };
    }

    public createCloseAction(event: SERPCloseEvent): CloseAction {
        return {
            action: SERP_ANALYTICS_ACTIONS_TYPE.CLOSE,
            queryString: event.detail.data.searchQuery,
            totalResultsCount: event.detail.data.resultsCount,
            serpType: event.detail.data.serpType,
        };
    }
}
